import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import styles from './Recognition.module.css';
import { Award, Trophy, Crown, Star, Zap, Medal } from 'lucide-react';
import LevelBadge from '../components/LevelBadge/LevelBadge';
import { getLevel, formatLevelBadge } from '../utils/levels';

interface EliteMember {
  id: number;
  username: string;
  avatarUrl: string | null;
  orgPoints: number;
  level: number;
  primaryPosition: string | null;
  department: string | null;
  motto: string | null;
  paragonBio: string | null;
  profileTheme: string | null;
  joinDate: string;
}

const Recognition: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'veterans' | 'masters' | 'legends' | 'paragons'>('veterans');
  const [veterans, setVeterans] = useState<EliteMember[]>([]);
  const [masters, setMasters] = useState<EliteMember[]>([]);
  const [legends, setLegends] = useState<EliteMember[]>([]);
  const [paragons, setParagons] = useState<EliteMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEliteMembers();
  }, []);

  const fetchEliteMembers = async () => {
    try {
      setLoading(true);
      // Fetch all elite members (Level 4+)
      const res = await axios.get('/auth/user/elite-members?minLevel=4');
      const members: EliteMember[] = res.data;

      // Separate into categories based on level
      const vets = members.filter(m => m.level === 4 || m.level === 5);
      const msts = members.filter(m => m.level >= 6 && m.level <= 8);
      const legs = members.filter(m => m.level === 9);
      const pars = members.filter(m => m.level >= 10);

      console.log('Elite Members Distribution:', {
        veterans: vets.map(m => ({ username: m.username, level: m.level, points: m.orgPoints })),
        masters: msts.map(m => ({ username: m.username, level: m.level, points: m.orgPoints })),
        legends: legs.map(m => ({ username: m.username, level: m.level, points: m.orgPoints })),
        paragons: pars.map(m => ({ username: m.username, level: m.level, points: m.orgPoints }))
      });

      setVeterans(vets);
      setMasters(msts);
      setLegends(legs);
      setParagons(pars);
    } catch (error) {
      console.error('Failed to fetch elite members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url: string | null) =>
    url?.startsWith('http') ? url : `${process.env.REACT_APP_API_URL}${url}`;

  const getAvatarBorderColor = (theme: string | null) => {
    if (!theme) return 'var(--accent-primary)';
    const themeColors: { [key: string]: string } = {
      'elite-blue': '#3b82f6',
      'master-gold': '#f59e0b',
      'legend-purple': '#a855f7'
    };
    return themeColors[theme] || 'var(--accent-primary)';
  };

  const renderMemberCard = (member: EliteMember, showBio: boolean = false) => (
    <div key={member.id} className={styles.memberCard}>
      <div className={styles.cardHeader}>
        <img
          src={getFullImageUrl(member.avatarUrl) || '/default-avatar.png'}
          alt={member.username}
          className={styles.avatar}
          style={{ borderColor: member.profileTheme ? getAvatarBorderColor(member.profileTheme) : 'var(--accent-primary)' }}
        />
        <div className={styles.memberInfo}>
          <h3 className={styles.memberName}>{member.username}</h3>
          <div className={styles.levelBadgeContainer}>
            <LevelBadge level={member.level} />
          </div>
          {member.primaryPosition && (
            <p className={styles.position}>
              {member.department && `${member.department} - `}{member.primaryPosition}
            </p>
          )}
        </div>
        <div className={styles.pointsBadge}>
          <span className={styles.points}>{member.orgPoints.toLocaleString()}</span>
          <span className={styles.pointsLabel}>ORG Points</span>
        </div>
      </div>
      
      {member.motto && (
        <div className={styles.motto}>
          <em>"{member.motto}"</em>
        </div>
      )}

      {showBio && member.paragonBio && (
        <div className={styles.paragonBio}>
          <p>{member.paragonBio}</p>
        </div>
      )}

      <div className={styles.memberFooter}>
        <span className={styles.joinDate}>
          Member since {new Date(member.joinDate).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
        </span>
      </div>
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.pageHeader}>
          <Trophy size={48} className={styles.headerIcon} />
          <div>
            <h1 className={styles.pageTitle}>Hall of Recognition</h1>
            <p className={styles.pageSubtitle}>
              Celebrating our most dedicated and accomplished members
            </p>
          </div>
        </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'veterans' ? styles.active : ''}`}
          onClick={() => setActiveTab('veterans')}
        >
          <Medal size={20} />
          <span>Veterans</span>
          <span className={styles.tabCount}>{veterans.length}</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'masters' ? styles.active : ''}`}
          onClick={() => setActiveTab('masters')}
        >
          <Zap size={20} />
          <span>Masters</span>
          <span className={styles.tabCount}>{masters.length}</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'legends' ? styles.active : ''}`}
          onClick={() => setActiveTab('legends')}
        >
          <Star size={20} />
          <span>Legends</span>
          <span className={styles.tabCount}>{legends.length}</span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'paragons' ? styles.active : ''}`}
          onClick={() => setActiveTab('paragons')}
        >
          <Crown size={20} />
          <span>Paragons</span>
          <span className={styles.tabCount}>{paragons.length}</span>
        </button>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>Loading elite members...</div>
        ) : (
          <>
            {activeTab === 'veterans' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Medal size={32} className={styles.sectionIcon} />
                  <div>
                    <h2>Veteran Showcase</h2>
                    <p>Level 4-5 Members: Experienced contributors who have proven their dedication</p>
                  </div>
                </div>
                <div className={styles.memberGrid}>
                  {veterans.length > 0 ? (
                    veterans.map(member => renderMemberCard(member))
                  ) : (
                    <p className={styles.emptyState}>No Veterans yet. Keep contributing to reach Level 4!</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'masters' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Zap size={32} className={styles.sectionIcon} />
                  <div>
                    <h2>Master Contributors</h2>
                    <p>Level 6-8 Members: Elite members with exceptional contributions</p>
                  </div>
                </div>
                <div className={styles.memberGrid}>
                  {masters.length > 0 ? (
                    masters.map(member => renderMemberCard(member))
                  ) : (
                    <p className={styles.emptyState}>No Masters yet. Achieve Level 6 to join this elite group!</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'legends' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Star size={32} className={styles.sectionIcon} />
                  <div>
                    <h2>Legend Board</h2>
                    <p>Level 9 Members: Legendary contributors who exemplify excellence</p>
                  </div>
                </div>
                <div className={styles.memberGrid}>
                  {legends.length > 0 ? (
                    legends.map(member => renderMemberCard(member))
                  ) : (
                    <p className={styles.emptyState}>No Legends yet. Push for Level 9 to join the elite!</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'paragons' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Crown size={32} className={styles.sectionIcon} />
                  <div>
                    <h2>Paragon Hall</h2>
                    <p>Level 10+ Members: The pinnacle of achievement and dedication</p>
                  </div>
                </div>
                <div className={styles.paragonGrid}>
                  {paragons.length > 0 ? (
                    paragons.map(member => renderMemberCard(member, true))
                  ) : (
                    <p className={styles.emptyState}>No Paragons yet. Reach Level 10 to join this prestigious hall!</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default Recognition;
